<template>
  <div class="relative" ref="datePickerContainer">
    <div class="flex items-center relative">
      <vs-input class="vsinput w-full" v-model="inputDate" :readonly="readonly" :placeholder="placeholder" :class="readonly || 'none-readonly-input'" :danger="danger" :danger-text="dangerText" @click="onInputClick" @keyup.enter="handleInputBlur" @blur="handleInputBlur" />
      <vs-icon
        v-if="!readonly && !date"
        icon-pack="feather"
        icon="icon-calendar"
        style="margin-top: 6px; margin-bottom: 6px; margin-right: 1px; padding-left: 6px; padding-right: 6px; position: absolute; top: 0; right: 0; font-size: 24px; border-left: 1px solid #cdcdcd;"
        class="cursor-pointer"
        @click="toggleDatePicker"
      ></vs-icon>
      <vs-icon
        v-else-if="!readonly && date"
        icon-pack="feather"
        icon="icon-x"
        style="margin-top: 6px; margin-bottom: 6px; margin-right: 1px; padding-left: 6px; padding-right: 6px; position: absolute; top: 0; right: 0; font-size: 24px; border-left: 1px solid #cdcdcd; background-color: #fff"
        class="cursor-pointer"
        @click="!readonly && clickXIcon()"
      ></vs-icon>
    </div>
    <div v-if="!readonly && viewDatePicker" class="date-picker-container flex flex-col items-center justify-center">
      <div class="ds-header flex items-center justify-center gap-3 mb-4">
        <!-- YEAR -->
        <div class="flex items-center justify-center gap-1">
          <button @click="onClickPrevYear"><feather-icon icon="ChevronLeftIcon" /></button>
          <select v-model="vcYear" @change="changeYear" class="year-select">
            <option
              v-for="year in yearOptions"
              :key="`year-${year}`"
              :value="year"
              :class="{ 'current-option': year === currentYear }"
              :style="year === currentYear ? 'font-weight: bold; background-color: #e3f2fd;' : ''"
            >
              {{ year }}년
            </option>
          </select>
          <button @click="onClickNextYear"><feather-icon icon="ChevronRightIcon" /></button>
        </div>
        <!-- MONTH -->
        <div class="flex items-center justify-center gap-1">
          <button @click="onClickPrevMonth"><feather-icon icon="ChevronLeftIcon" /></button>
          <select v-model="vcMonth" @change="changeMonth" class="month-select">
            <option
              v-for="month in 12"
              :key="`month-${month}`"
              :value="month"
              :class="{ 'current-option': month === currentMonth && vcYear === currentYear }"
              :style="month === currentMonth && vcYear === currentYear ? 'font-weight: bold; background-color: #e3f2fd;' : ''"
            >
              {{ month }}월
            </option>
          </select>
          <button @click="onClickNextMonth"><feather-icon icon="ChevronRightIcon" /></button>
        </div>
      </div>
      <vc-date-picker ref="vc-dp" :value="dateOBJ" :inline="true" class="vc-date-picker inline-block" :masks="masks" :disabled="readonly" :min-date="minDate" :max-date="maxDate" @dayclick="updateDate" />
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import * as lang from "vuejs-datepicker/src/locale";

export default {
  name: "DateSelector",
  components: {
    Datepicker,
  },
  props: {
    value: { type: String, default: "" },
    format: { type: String, default: "YYYY-MM-DD" },
    minimumView: { type: String, default: "day" },
    maximumView: { type: String, default: "year" },
    minDate: { type: String, default: "" },
    maxDate: { type: String, default: "" },
    readonly: Boolean,
    placeholder: String,
    danger: Boolean,
    dangerText: String,
  },
  data() {
    return {
      languages: lang,
      masks: {
        input: this.format,
      },
      viewDatePicker: false,
      isInputFocused: false,

      vcYear: new Date().getFullYear(),
      vcMonth: new Date().getMonth() + 1,

      inputDate: "",
      dateOBJ: "",
      date: "",
    };
  },
  computed: {
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const selectedYear = this.vcYear;
      const years = [];

      // 선택된 년도를 중심으로 적절한 범위 설정
      const minYear = Math.min(currentYear - 50, selectedYear - 30);
      const maxYear = Math.max(currentYear + 10, selectedYear + 30);

      for (let year = minYear; year <= maxYear; year++) {
        years.push(year);
      }
      return years;
    },
    currentYear() {
      return new Date().getFullYear();
    },
    currentMonth() {
      return new Date().getMonth() + 1;
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.date = new Date(value).format("yyyy-MM-dd");
          this.inputDate = new Date(value).format("yyyy-MM-dd");
          this.$set(this, "dateOBJ", new Date(value));
        } else {
          this.date = "";
        }
      },
    },
    date: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
        this.inputDate = value;
        this.$set(this, "dateOBJ", new Date(value));
      },
    },
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    toggleDatePicker() {
      if (!this.readonly && !this.isInputFocused) {
        this.viewDatePicker = !this.viewDatePicker;
        this.isInputFocused = false; // Ensure input focus is reset after toggle
      }
    },
    onInputClick() {
      if (!this.viewDatePicker) {
        this.viewDatePicker = true;
      } else {
        this.isInputFocused = true;
      }
    },
    onClickPrevYear() {
      this.vcYear = this.vcYear - 1;
      this.$nextTick(() => {
        this.moveVCDP();
      });
    },
    onClickNextYear() {
      this.vcYear = this.vcYear + 1;
      this.$nextTick(() => {
        this.moveVCDP();
      });
    },
    onClickPrevMonth() {
      if (this.vcMonth === 1) this.vcMonth = 12;
      else --this.vcMonth;
      this.moveVCDP();
    },
    onClickNextMonth() {
      if (this.vcMonth === 12) this.vcMonth = 1;
      else ++this.vcMonth;
      this.moveVCDP();
    },
    changeMonth() {
      this.moveVCDP();
    },
    changeYear() {
      this.moveVCDP();
    },
    moveVCDP() {
      this.$refs["vc-dp"].move(new Date(this.vcYear, this.vcMonth - 1, 1));
    },
    updateDate(data) {
      this.date = new Date(data.date).format("yyyy-MM-dd");
      this.viewDatePicker = false; // 날짜 선택 후 달력 닫기
    },
    clickXIcon() {
      this.date = "";
    },

    handleInputBlur() {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      const compactRegex = /^\d{8}$/;

      let parsedDate = null;

      if (dateRegex.test(this.inputDate)) {
        parsedDate = new Date(this.inputDate);
      } else if (compactRegex.test(this.inputDate)) {
        const year = this.inputDate.slice(0, 4);
        const month = this.inputDate.slice(4, 6);
        const day = this.inputDate.slice(6, 8);
        parsedDate = new Date(`${year}-${month}-${day}`);
      }

      if (parsedDate && parsedDate instanceof Date && !isNaN(parsedDate)) {
        this.vcYear = parsedDate.getFullYear();
        this.vcMonth = parsedDate.getMonth() + 1;

        this.$set(this, "dateOBJ", new Date(parsedDate));
        this.moveVCDP();
        this.date = parsedDate.toISOString().split("T")[0];
      } else {
        this.date = "";
      }

      this.inputDate = this.date;
      this.isInputFocused = false;
    },
    handleOutsideClick(event) {
      if (this.viewDatePicker && !this.$refs.datePickerContainer.contains(event.target)) {
        this.viewDatePicker = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.none-readonly-input::v-deep {
  input {
    background: white;
  }
}

.date-picker-container {
  position: absolute;
  top: 50px;
  left: 0;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  padding: 10px;
  z-index: 53000;

  button {
    width: 20px;
    height: 20px;
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 16px;
      height: 16px;
        }
  }

    select {
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    padding: 2px 4px;
    text-align: center;
    font-size: 14px;
  }

  .year-select {
    width: 80px;
  }

  .month-select {
    width: 50px;
  }

  .current-option {
    font-weight: bold !important;
    background-color: #e3f2fd !important;
    color: #1976d2 !important;
  }

  .vc-date-picker::v-deep {
    border: 0;
    .vc-header,
    .vc-arrows-container,
    .in-next-month {
      display: none;
    }
  }
}
</style>
